import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import ABTestDrawer from '../components/ABTestDrawer';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import '../styles/design-system.css';
import './ABTestsPage.css';

/* ============================================================
   ICONS
   ============================================================ */
const IP = {
  viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
  strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round',
};
const Icon = {
  Plus:    (p) => (<svg width="14" height="14" {...IP} strokeWidth="2.5" {...p}><path d="M5 12h14M12 5v14"/></svg>),
  Flask:   (p) => (<svg width="40" height="40" {...IP} strokeWidth="1.5" {...p}><path d="M10 2v7L4.5 19a2 2 0 0 0 1.8 3h11.4a2 2 0 0 0 1.8-3L14 9V2"/><path d="M8 2h8"/></svg>),
  BarChart:(p) => (<svg width="13" height="13" {...IP} {...p}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>),
  Copy:    (p) => (<svg width="13" height="13" {...IP} {...p}><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>),
  Pause:   (p) => (<svg width="13" height="13" {...IP} {...p}><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>),
  Play:    (p) => (<svg width="13" height="13" {...IP} {...p}><polygon points="5 3 19 12 5 21 5 3"/></svg>),
  Trophy:  (p) => (<svg width="13" height="13" {...IP} {...p}><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2z"/></svg>),
  Trash:   (p) => (<svg width="13" height="13" {...IP} {...p}><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>),
  X:       (p) => (<svg width="14" height="14" {...IP} strokeWidth="2.5" {...p}><path d="M18 6 6 18M6 6l12 12"/></svg>),
};

/* ============================================================
   HELPERS
   ============================================================ */
const FAV_VARIANTS = ['', 'olive', 'dark', 'mustard'];

function deriveStatus(ab) {
  if (!ab) return 'draft';
  if (ab.winner_declared || ab.winner_index != null) return 'winner';
  if (ab.enabled) return 'running';
  return 'draft';
}
function totalTraffic(ab) {
  if (!ab?.variants?.length) return 0;
  return ab.variants.reduce((s, v) => s + (v.clicks || 0), 0);
}
function bestLift(ab) {
  if (!ab?.variants?.length) return null;
  const lifts = ab.variants.map((v) => v.lift).filter((l) => l != null && !isNaN(l));
  return lifts.length ? Math.max(...lifts) : null;
}
function variantDesc(ab) {
  if (!ab?.variants?.length) return '—';
  return ab.variants.map((v) => v.url || '?').join(' vs ');
}
function genSparkline(seed, status) {
  let s = (seed || 0) + (status === 'running' ? 1 : status === 'winner' ? 2 : 3);
  const rand = () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
  const W = 80, H = 28, N = 8;
  const pts = [];
  let y = H * 0.75;
  for (let i = 0; i < N; i++) {
    const trend = status === 'draft' ? 0 : -0.12;
    y = Math.max(4, Math.min(H - 4, y + (rand() - 0.5 + trend) * 10));
    pts.push([((i / (N - 1)) * W).toFixed(1), y.toFixed(1)]);
  }
  const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x},${y}`).join(' ');
  const area = `${line} L80,28 L0,28 Z`;
  const color = status === 'winner' ? '#ff5436' : '#8b9b3f';
  return { line, area, color };
}
function strSeed(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}
function normaliseLink(link, i) {
  const ab = link.ab_test || {};
  const status = deriveStatus(ab);
  const traffic = totalTraffic(ab) || link.total_clicks || 0;
  const lift = bestLift(ab);
  return {
    _linkId: link.link_id,
    id: link.link_id,
    slug: link.slug || '?',
    dest: link.destination_url || '',
    variantCount: ab.variants?.length || 2,
    variantDesc: variantDesc(ab),
    traffic, lift,
    liftPositive: lift != null ? lift > 0 : null,
    status, favicon: FAV_VARIANTS[i % 4], ab,
  };
}

/* ============================================================
   STATUS PILL
   ============================================================ */
function StatusPill({ status }) {
  return <span className={`ab-status ${status}`}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>;
}

/* ============================================================
   SPARKLINE
   ============================================================ */
function Sparkline({ seed, status }) {
  const { line, area, color } = genSparkline(strSeed(seed), status);
  const gradId = `sg-${seed.replace(/\W/g, '')}`;
  return (
    <svg className="ab-spark" viewBox="0 0 80 28" preserveAspectRatio="none">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradId})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/* ============================================================
   NEW TEST MODAL
   ============================================================ */
function NewTestModal({ open, onClose, workspaceId, onCreated }) {
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [selectedLink, setSelectedLink] = useState('');
  const [variantA, setVariantA] = useState('');
  const [variantB, setVariantB] = useState('');
  const [split, setSplit] = useState(50);
  const [startNow, setStartNow] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open || !workspaceId) return;
    setLoadingLinks(true);
    supabase
      .from('links')
      .select('link_id, slug, destination_url')
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false })
      .limit(100)
      .then(({ data }) => {
        setLinks(data || []);
        setLoadingLinks(false);
      });
  }, [open, workspaceId]);

  const filteredLinks = links.filter(l =>
    !search || l.slug?.toLowerCase().includes(search.toLowerCase()) ||
    l.destination_url?.toLowerCase().includes(search.toLowerCase())
  );

  const reset = () => {
    setSelectedLink(''); setVariantA(''); setVariantB('');
    setSplit(50); setStartNow(true); setSearch('');
  };

  const handleClose = () => { reset(); onClose(); };

  const handleCreate = async () => {
    if (!selectedLink || !variantA.trim() || !variantB.trim()) return;
    setSaving(true);
    try {
      const ab_test = {
        enabled: startNow,
        created_at: new Date().toISOString(),
        variants: [
          { url: variantA.trim(), weight: split, clicks: 0, lift: null, confidence: null },
          { url: variantB.trim(), weight: 100 - split, clicks: 0, lift: null, confidence: null },
        ],
      };
      const { error } = await supabase
        .from('links')
        .update({ ab_test })
        .eq('link_id', selectedLink);
      if (error) throw error;
      toast.success('A/B test created!');
      handleClose();
      onCreated();
    } catch (err) {
      toast.error(err.message || 'Failed to create test');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const canCreate = selectedLink && variantA.trim() && variantB.trim();

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(13,13,15,0.4)', animation: 'ab-backdrop-in 0.2s ease' }}
      />
      {/* Modal */}
      <div style={{
        position: 'relative', zIndex: 1, background: 'var(--surface)',
        border: '1px solid var(--line)', borderRadius: 16,
        width: 500, maxWidth: '95vw', maxHeight: '90vh', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(13,13,15,0.18)',
        animation: 'ab-backdrop-in 0.2s ease',
      }}>
        {/* Header */}
        <div style={{ padding: '22px 24px 18px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: "'Geist Mono',monospace", fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--ink-faint)', marginBottom: 6 }}>
              New experiment
            </div>
            <h2 style={{ fontFamily: "'Instrument Serif',serif", fontSize: 22, letterSpacing: '-0.02em', color: 'var(--ink)', margin: 0 }}>
              Create <em style={{ color: 'var(--coral)', fontStyle: 'italic' }}>A/B test</em>
            </h2>
          </div>
          <button type="button" onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-faint)', padding: 4, borderRadius: 6, display: 'flex' }}>
            <Icon.X />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Pick link */}
          <div>
            <label style={{ display: 'block', fontFamily: "'Geist Mono',monospace", fontSize: 11, color: 'var(--ink-dim)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Select link to test
            </label>
            <input
              type="text"
              placeholder="Search by slug or URL…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', background: 'var(--bg-warm)', border: '1px solid var(--line-strong)', borderRadius: 9, fontSize: 13, color: 'var(--ink)', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', marginBottom: 8 }}
            />
            {loadingLinks ? (
              <div style={{ padding: '12px 0', textAlign: 'center', fontFamily: "'Geist Mono',monospace", fontSize: 12, color: 'var(--ink-faint)' }}>Loading links…</div>
            ) : filteredLinks.length === 0 ? (
              <div style={{ padding: '12px 0', textAlign: 'center', fontFamily: "'Geist Mono',monospace", fontSize: 12, color: 'var(--ink-faint)' }}>No links found. Create a link first.</div>
            ) : (
              <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 9, background: 'var(--surface)' }}>
                {filteredLinks.map((l) => (
                  <div
                    key={l.link_id}
                    onClick={() => { setSelectedLink(l.link_id); setVariantA(l.destination_url || ''); }}
                    style={{
                      padding: '10px 14px', cursor: 'pointer',
                      background: selectedLink === l.link_id ? 'var(--coral-soft)' : 'transparent',
                      borderBottom: '1px solid var(--line)',
                      transition: 'background 0.1s',
                    }}
                  >
                    <div style={{ fontFamily: "'Geist Mono',monospace", fontSize: 12, color: selectedLink === l.link_id ? 'var(--coral-deep)' : 'var(--ink)', fontWeight: 600 }}>/{l.slug}</div>
                    <div style={{ fontFamily: "'Geist Mono',monospace", fontSize: 10, color: 'var(--ink-faint)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.destination_url}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Variant A */}
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: "'Geist Mono',monospace", fontSize: 11, color: 'var(--ink-dim)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              <span style={{ width: 18, height: 18, borderRadius: 4, background: '#ff5436', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 9, fontWeight: 700, flexShrink: 0 }}>A</span>
              Variant A URL
            </label>
            <input
              type="url"
              placeholder="https://example.com/page-a"
              value={variantA}
              onChange={(e) => setVariantA(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', background: 'var(--bg-warm)', border: '1px solid var(--line-strong)', borderRadius: 9, fontSize: 13, color: 'var(--ink)', fontFamily: "'Geist Mono',monospace", outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          {/* Variant B */}
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: "'Geist Mono',monospace", fontSize: 11, color: 'var(--ink-dim)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              <span style={{ width: 18, height: 18, borderRadius: 4, background: '#8b9b3f', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 9, fontWeight: 700, flexShrink: 0 }}>B</span>
              Variant B URL
            </label>
            <input
              type="url"
              placeholder="https://example.com/page-b"
              value={variantB}
              onChange={(e) => setVariantB(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', background: 'var(--bg-warm)', border: '1px solid var(--line-strong)', borderRadius: 9, fontSize: 13, color: 'var(--ink)', fontFamily: "'Geist Mono',monospace", outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          {/* Traffic split */}
          <div>
            <label style={{ display: 'block', fontFamily: "'Geist Mono',monospace", fontSize: 11, color: 'var(--ink-dim)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Traffic split
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontFamily: "'Geist Mono',monospace", fontSize: 12, color: '#ff5436', minWidth: 32, textAlign: 'center', fontWeight: 600 }}>{split}%</span>
              <input
                type="range"
                min={10} max={90} value={split}
                onChange={(e) => setSplit(Number(e.target.value))}
                style={{ flex: 1, accentColor: 'var(--coral)' }}
              />
              <span style={{ fontFamily: "'Geist Mono',monospace", fontSize: 12, color: '#8b9b3f', minWidth: 32, textAlign: 'center', fontWeight: 600 }}>{100 - split}%</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontFamily: "'Geist Mono',monospace", fontSize: 10, color: 'var(--ink-faint)' }}>
              <span>A gets {split}%</span><span>B gets {100 - split}%</span>
            </div>
          </div>

          {/* Start now toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: 'var(--bg-warm)', border: '1px solid var(--line)', borderRadius: 10 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginBottom: 2 }}>Start immediately</div>
              <div style={{ fontFamily: "'Geist Mono',monospace", fontSize: 11, color: 'var(--ink-faint)' }}>Begin routing traffic when created</div>
            </div>
            <button
              type="button"
              onClick={() => setStartNow(v => !v)}
              style={{
                width: 42, height: 24, borderRadius: 100, border: 'none', cursor: 'pointer',
                background: startNow ? 'var(--olive)' : 'var(--line-strong)',
                position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                padding: 0,
              }}
              aria-pressed={startNow}
            >
              <span style={{
                position: 'absolute', top: 3, left: startNow ? 21 : 3,
                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }} />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--line)', display: 'flex', gap: 8, background: 'var(--bg-warm)' }}>
          <button
            type="button"
            className="btn-coral"
            onClick={handleCreate}
            disabled={!canCreate || saving}
            style={{ opacity: canCreate && !saving ? 1 : 0.45 }}
          >
            {saving ? 'Creating…' : 'Create test'}
          </button>
          <button type="button" className="btn-outline" onClick={handleClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   MAIN PAGE
   ============================================================ */
export default function ABTestsPage() {
  const { workspace } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTest, setSelectedTest] = useState(null);
  const [showNewTest, setShowNewTest] = useState(false);

  const activeFilter = searchParams.get('status') || 'all';
  const setFilter = (s) => {
    const next = new URLSearchParams(searchParams);
    if (s === 'all') next.delete('status'); else next.set('status', s);
    setSearchParams(next, { replace: true });
  };

  /* ----------------------------------------------------------
     Load tests
     ---------------------------------------------------------- */
  const fetchTests = useCallback(async () => {
    if (!workspace?.workspace_id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('links')
        .select('link_id, slug, destination_url, ab_test, created_at, total_clicks')
        .eq('workspace_id', workspace.workspace_id)
        .not('ab_test', 'is', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const real = (data || []).map(normaliseLink);
      // Always show mock data when there are no real tests so the UI is understandable
      setTests(real.length > 0 ? real : FALLBACK_TESTS);
    } catch (err) {
      console.error('Failed to load A/B tests', err);
      setTests(FALLBACK_TESTS);
    } finally {
      setLoading(false);
    }
  }, [workspace?.workspace_id]);

  useEffect(() => { fetchTests(); }, [fetchTests]);

  /* ----------------------------------------------------------
     Delete (remove ab_test from link)
     ---------------------------------------------------------- */
  const handleDelete = useCallback(async (t) => {
    if (t._linkId == null) { toast.error('Cannot delete mock test'); return; }
    if (!window.confirm(`Delete A/B test on "${t.slug}"?`)) return;
    const { error } = await supabase
      .from('links').update({ ab_test: null }).eq('link_id', t._linkId);
    if (error) { toast.error(error.message); return; }
    toast.success('Test deleted');
    if (selectedTest?.id === t.id) setSelectedTest(null);
    fetchTests();
  }, [selectedTest, fetchTests]);

  /* ----------------------------------------------------------
     Derived stats (from full unfiltered list)
     ---------------------------------------------------------- */
  const statsRunning  = tests.filter((t) => t.status === 'running').length;
  const statsWinners  = tests.filter((t) => t.status === 'winner').length;
  const statsTotalTraffic = tests.reduce((s, t) => s + t.traffic, 0);
  const liftValues    = tests.filter((t) => t.lift != null).map((t) => t.lift);
  const statsAvgLift  = liftValues.length
    ? liftValues.reduce((s, l) => s + l, 0) / liftValues.length
    : null;

  /* ----------------------------------------------------------
     Filtered rows
     ---------------------------------------------------------- */
  const filteredTests = activeFilter === 'all'
    ? tests
    : tests.filter((t) => t.status === activeFilter);

  /* ----------------------------------------------------------
     Render
     ---------------------------------------------------------- */
  return (
    <div className="ds-page" data-testid="ab-tests-page">

      {/* ── Header ── */}
      <div className="page-header">
        <div>
          <div className="page-label">Insights · Experimentation</div>
          <h1 className="page-title">A/B <em>tests</em>.</h1>
          <p className="page-sub">Run split-traffic experiments and let LinkFlow pick the winning route.</p>
        </div>
        <button type="button" className="btn-coral" onClick={() => setShowNewTest(true)}>
          <Icon.Plus /> New test
        </button>
      </div>

      {/* ── Stats strip ── */}
      {!loading && tests.length > 0 && (
        <div className="ab-stats-strip">
          <div className="ab-stat primary">
            <div className="ab-stat-label">Total tests</div>
            <div className="ab-stat-value">{tests.length}</div>
            <div className="ab-stat-meta">{statsRunning} running now</div>
          </div>
          <div className="ab-stat">
            <div className="ab-stat-label">Running</div>
            <div className="ab-stat-value">{statsRunning}</div>
            <div className="ab-stat-meta">active experiments</div>
          </div>
          <div className="ab-stat">
            <div className="ab-stat-label">Avg lift</div>
            <div className={`ab-stat-value${statsAvgLift != null && statsAvgLift > 0 ? ' olive' : ''}`}>
              {statsAvgLift != null ? `+${statsAvgLift.toFixed(1)}%` : '—'}
            </div>
            <div className="ab-stat-meta">{statsWinners} winner{statsWinners !== 1 ? 's' : ''} declared</div>
          </div>
          <div className="ab-stat">
            <div className="ab-stat-label">Total traffic</div>
            <div className="ab-stat-value">{statsTotalTraffic >= 1000 ? `${(statsTotalTraffic / 1000).toFixed(1)}k` : statsTotalTraffic}</div>
            <div className="ab-stat-meta">clicks across all tests</div>
          </div>
        </div>
      )}

      {/* ── Skeleton while loading ── */}
      {loading && (
        <div className="full-table-wrap">
          {[1, 2, 3].map((i) => (
            <div key={i} style={{ padding: '18px 22px', borderBottom: '1px solid var(--line)', display: 'flex', gap: 14, alignItems: 'center' }}>
              <div className="ds-skeleton" style={{ width: 36, height: 36, borderRadius: 9 }} />
              <div style={{ flex: 1 }}>
                <div className="ds-skeleton" style={{ height: 13, width: 200, marginBottom: 6 }} />
                <div className="ds-skeleton" style={{ height: 10, width: 140 }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Drawer ── */}
      <ABTestDrawer test={selectedTest} onClose={() => setSelectedTest(null)} />

      {/* ── New test modal ── */}
      <NewTestModal
        open={showNewTest}
        onClose={() => setShowNewTest(false)}
        workspaceId={workspace?.workspace_id}
        onCreated={fetchTests}
      />

      {/* ── Table ── */}
      {!loading && tests.length > 0 && (
        <div className="full-table-wrap">

          {/* Toolbar with live filter chips */}
          <div className="ab-toolbar">
            <div className="ab-filter-chips">
              <button
                type="button"
                className={`ab-chip${activeFilter === 'all' ? ' active' : ''}`}
                onClick={() => setFilter('all')}
              >
                All <span className="ab-chip-count">{tests.length}</span>
              </button>
              {['running', 'winner', 'draft', 'paused'].map((s) => {
                const n = tests.filter((t) => t.status === s).length;
                if (n === 0) return null;
                return (
                  <button
                    key={s}
                    type="button"
                    className={`ab-chip${activeFilter === s ? ' active' : ''}`}
                    onClick={() => setFilter(s)}
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}{' '}
                    <span className="ab-chip-count">{n}</span>
                  </button>
                );
              })}
            </div>
            <span style={{ fontFamily: "'Geist Mono',monospace", fontSize: 11, color: 'var(--ink-dim)' }}>
              Sort: <strong style={{ color: 'var(--ink-2)', fontWeight: 500 }}>Lift ↓</strong>
            </span>
          </div>

          {/* Head */}
          <div className="ab-table-head">
            <div>Test</div>
            <div>Variants</div>
            <div>Traffic</div>
            <div>Lift</div>
            <div>Trend</div>
            <div>Status</div>
            <div />
          </div>

          {/* No results for filter */}
          {filteredTests.length === 0 && (
            <div style={{ padding: '40px 22px', textAlign: 'center', fontFamily: "'Geist Mono',monospace", fontSize: 13, color: 'var(--ink-faint)' }}>
              No {activeFilter} tests.{' '}
              <button
                type="button"
                onClick={() => setFilter('all')}
                style={{ color: 'var(--coral)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}
              >
                Clear filter
              </button>
            </div>
          )}

          {/* Rows */}
          {filteredTests.map((t) => {
            const liftLabel = t.lift != null
              ? `${t.lift > 0 ? '+' : ''}${t.lift.toFixed(1)}%`
              : '—';
            const liftCls = t.liftPositive === true ? 'up' : t.liftPositive === false ? 'down' : 'neutral';
            const isActive = selectedTest?.id === t.id;

            return (
              <div
                key={t.id}
                className={`ab-row${isActive ? ' ab-active' : ''}`}
                data-testid={`ab-row-${t.slug}`}
                onClick={() => setSelectedTest(t)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setSelectedTest(t)}
              >
                {/* Test name */}
                <div className="ab-test-cell">
                  <div className={`ab-favicon ${t.favicon}`}>
                    {t.slug.charAt(0).toUpperCase()}
                  </div>
                  <div className="ab-test-info">
                    <div className="ab-test-name">{t.slug} <em>·</em> test</div>
                    <div className="ab-test-sub">{t.variantDesc}</div>
                  </div>
                </div>

                {/* Variant count */}
                <div style={{ fontFamily: "'Geist Mono',monospace", fontSize: 13, color: 'var(--ink-2)' }}>
                  {t.variantCount}
                </div>

                {/* Traffic */}
                <div style={{ fontFamily: "'Geist Mono',monospace", fontSize: 13, color: 'var(--ink-2)', fontFeatureSettings: "'tnum' 1" }}>
                  {t.traffic.toLocaleString()}
                </div>

                {/* Lift */}
                <div className={`ab-lift ${liftCls}`}>{liftLabel}</div>

                {/* Sparkline */}
                <Sparkline seed={t.slug} status={t.status} />

                {/* Status */}
                <StatusPill status={t.status} />

                {/* Kebab */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="ab-more"
                      aria-label="Actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      ⋯
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem
                      onSelect={() => { setSelectedTest(t); }}
                    >
                      <Icon.BarChart style={{ marginRight: 8 }} /> View detail
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => {
                        if (t._linkId) navigate(`/links/${t._linkId}`);
                        else toast.error('No link ID (mock data)');
                      }}
                    >
                      <Icon.BarChart style={{ marginRight: 8 }} /> Open analytics
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => {
                        const url = `${window.location.origin}/${t.slug}`;
                        navigator.clipboard.writeText(url);
                        toast.success('Link URL copied');
                      }}
                    >
                      <Icon.Copy style={{ marginRight: 8 }} /> Copy link URL
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    {t.status === 'running' && (
                      <DropdownMenuItem disabled>
                        <Icon.Pause style={{ marginRight: 8 }} /> Pause test
                        {/* TODO(ab-kebab): wire to update ab_test.enabled = false */}
                      </DropdownMenuItem>
                    )}
                    {t.status === 'draft' && (
                      <DropdownMenuItem disabled>
                        <Icon.Play style={{ marginRight: 8 }} /> Launch test
                        {/* TODO(ab-kebab): wire to update ab_test.enabled = true */}
                      </DropdownMenuItem>
                    )}
                    {t.status === 'running' && (
                      <DropdownMenuItem disabled>
                        <Icon.Trophy style={{ marginRight: 8 }} /> Declare winner
                        {/* TODO(ab-kebab): wire to declareWinner RPC */}
                      </DropdownMenuItem>
                    )}

                    <DropdownMenuSeparator />

                    <DropdownMenuItem
                      onSelect={() => handleDelete(t)}
                      style={{ color: 'var(--coral-deep)' }}
                    >
                      <Icon.Trash style={{ marginRight: 8 }} /> Delete test
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   FALLBACK MOCK DATA — shown when workspace has no real A/B tests
   ============================================================ */
const FALLBACK_TESTS = [
  {
    id: 'mock-1', slug: 'spring-26', dest: 'https://example.com/v1',
    variantCount: 3, variantDesc: 'example.com/v1 vs /v2 vs /control',
    traffic: 42871, lift: 18.4, liftPositive: true,
    status: 'running', favicon: '', _linkId: null,
    ab: {
      enabled: true,
      created_at: '2026-04-01T10:00:00Z',
      variants: [
        { url: 'https://example.com/landing-v1', weight: 34, clicks: 14820, lift: 18.4, confidence: 97.2 },
        { url: 'https://example.com/landing-v2', weight: 33, clicks: 13901, lift: 11.2, confidence: 89.4 },
        { url: 'https://example.com/landing-control', weight: 33, clicks: 14150, lift: 0, confidence: null },
      ],
    },
  },
  {
    id: 'mock-2', slug: 'webinar-may', dest: 'https://example.com/reg',
    variantCount: 2, variantDesc: '"Register now" vs "Save my seat"',
    traffic: 28419, lift: 9.2, liftPositive: true,
    status: 'winner', favicon: 'olive', _linkId: null,
    ab: {
      enabled: false, winner_declared: true, winner_index: 1,
      created_at: '2026-03-15T09:00:00Z',
      variants: [
        { url: 'https://example.com/reg?cta=register', weight: 50, clicks: 12834, lift: 0, confidence: null },
        { url: 'https://example.com/reg?cta=save-seat', weight: 50, clicks: 15585, lift: 9.2, confidence: 98.1, is_winner: true },
      ],
    },
  },
  {
    id: 'mock-3', slug: 'blog-pricing', dest: 'https://example.com/pricing',
    variantCount: 2, variantDesc: 'pricing-new vs pricing-original',
    traffic: 0, lift: null, liftPositive: null,
    status: 'draft', favicon: 'mustard', _linkId: null,
    ab: {
      enabled: false,
      created_at: '2026-05-10T14:30:00Z',
      variants: [
        { url: 'https://example.com/pricing-new', weight: 50, clicks: 0, lift: null, confidence: null },
        { url: 'https://example.com/pricing-original', weight: 50, clicks: 0, lift: null, confidence: null },
      ],
    },
  },
  {
    id: 'mock-4', slug: 'product-hunt', dest: 'https://example.com/ph',
    variantCount: 2, variantDesc: 'ph-v1 vs ph-v2',
    traffic: 8342, lift: -2.1, liftPositive: false,
    status: 'paused', favicon: 'dark', _linkId: null,
    ab: {
      enabled: false,
      created_at: '2026-02-20T08:00:00Z',
      variants: [
        { url: 'https://example.com/ph-launch', weight: 50, clicks: 4371, lift: 0, confidence: null },
        { url: 'https://example.com/ph-waitlist', weight: 50, clicks: 3971, lift: -2.1, confidence: 61.0 },
      ],
    },
  },
];
